import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

/**
 * Single shared WebSocket connection for the whole app.
 * Replaces the per-component useWebSocket() pattern that was opening
 * multiple simultaneous connections and causing reconnect storms.
 *
 * Usage:
 *   1. Wrap your app: <WebSocketProvider>...</WebSocketProvider>
 *   2. In any component: const { subscribe, isConnected, sendMessage } = useWebSocketContext();
 *      useEffect(() => subscribe(handler), [subscribe]);
 */

const WebSocketContext = createContext({
  isConnected: false,
  subscribe: () => () => {},
  sendMessage: () => {},
  sendTypingIndicator: () => {},
  sendReadReceipt: () => {},
});

const RECONNECT_INTERVAL = 3000;
const MAX_RECONNECT_ATTEMPTS = 8;
const PING_INTERVAL = 10000; // 10s — keep ahead of typical proxy idle timeouts

export const WebSocketProvider = ({ children }) => {
  const ws = useRef(null);
  const subscribers = useRef(new Set());
  const reconnectAttempts = useRef(0);
  const reconnectTimeout = useRef(null);
  const pingTimer = useRef(null);
  const tokenAtConnect = useRef(null);
  const [isConnected, setIsConnected] = useState(false);

  const getWebSocketUrl = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
    return `${backendUrl.replace(/^https?/, wsProtocol)}/api/ws/${token}`;
  }, []);

  const connect = useCallback(() => {
    const url = getWebSocketUrl();
    if (!url) return;
    if (ws.current?.readyState === WebSocket.OPEN || ws.current?.readyState === WebSocket.CONNECTING) {
      return; // Already connected/connecting — don't open a duplicate
    }
    tokenAtConnect.current = localStorage.getItem('token');
    try {
      ws.current = new WebSocket(url);

      ws.current.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.current.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        // Fan out to every subscriber
        subscribers.current.forEach((cb) => {
          try { cb(data); } catch (e) { console.error('WS subscriber error:', e); }
        });
      };

      ws.current.onclose = (event) => {
        setIsConnected(false);
        if (event.code !== 1000 && reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          // Exponential backoff: 3s, 6s, 12s, 24s, max 30s
          const delay = Math.min(30000, RECONNECT_INTERVAL * Math.pow(2, reconnectAttempts.current));
          reconnectAttempts.current += 1;
          reconnectTimeout.current = setTimeout(connect, delay);
        }
      };

      ws.current.onerror = () => {
        // Errors are followed by onclose, which triggers reconnect logic
      };
    } catch (e) {
      console.error('Failed to create WebSocket:', e);
    }
  }, [getWebSocketUrl]);

  // Connect once on mount (if logged in) and on token change
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) connect();

    const onStorage = () => {
      const newToken = localStorage.getItem('token');
      if (newToken !== tokenAtConnect.current) {
        // Token changed (login/logout) — close & reconnect
        if (ws.current) {
          ws.current.close(1000, 'Token changed');
          ws.current = null;
        }
        if (newToken) connect();
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('storage', onStorage);
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (pingTimer.current) clearInterval(pingTimer.current);
      if (ws.current) {
        ws.current.close(1000, 'Provider unmount');
        ws.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep-alive ping every 10s while connected
  useEffect(() => {
    if (!isConnected) return undefined;
    pingTimer.current = setInterval(() => {
      if (ws.current?.readyState === WebSocket.OPEN) {
        try { ws.current.send(JSON.stringify({ type: 'ping' })); } catch { /* ignore */ }
      }
    }, PING_INTERVAL);
    return () => clearInterval(pingTimer.current);
  }, [isConnected]);

  const subscribe = useCallback((handler) => {
    if (typeof handler !== 'function') return () => {};
    subscribers.current.add(handler);
    return () => subscribers.current.delete(handler);
  }, []);

  const sendMessage = useCallback((data) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      try { ws.current.send(JSON.stringify(data)); } catch { /* ignore */ }
    }
  }, []);

  const sendTypingIndicator = useCallback((partnerId, isTyping) => {
    sendMessage({ type: 'typing', partner_id: partnerId, is_typing: isTyping });
  }, [sendMessage]);

  const sendReadReceipt = useCallback((partnerId) => {
    sendMessage({ type: 'read_receipt', partner_id: partnerId });
  }, [sendMessage]);

  return (
    <WebSocketContext.Provider value={{ isConnected, subscribe, sendMessage, sendTypingIndicator, sendReadReceipt }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocketContext = () => useContext(WebSocketContext);

/** Convenience hook: subscribe to WS messages. Auto-unsubscribes on unmount. */
export const useWsSubscribe = (handler) => {
  const { subscribe } = useWebSocketContext();
  useEffect(() => subscribe(handler), [subscribe, handler]);
};
